<script setup lang="ts">
import { register } from 'swiper/element/bundle';
import { nextTick, watch } from 'vue';
import 'swiper/css';
import 'swiper/css/navigation';
import { useStoriesStore } from '@/stores/apostatudo/stories';
import { useStoryTracking } from '~/composables/apostatudo/use-story-tracking';

// Registra os web components do Swiper
register();

const emit = defineEmits(['close']);

const storiesStore = useStoriesStore();
const { stories, hasStories, lastSlideIndex } = storeToRefs(storiesStore);
const { markStoriesAsSeen, saveLastSlideIndex } = storiesStore;

const currentSlide = ref(0);
// progress used by the old implementation; not required with CSS keyframes
const progressBars = ref<(HTMLDivElement | null)[]>([]);

const onProgressEnd = (index: number) => {
  if (index !== currentSlide.value) return;
  if (swiperRef.value) {
    if (swiperRef.value.isEnd) {
      // Marca como visto apenas quando termina o último slide naturalmente
      markStoriesAsSeen();
      closeStories();
    } else {
      swiperRef.value.slideNext();
    }
  }
};

// helper to set element refs from template
const setProgressRef = (
  el: Element | ComponentPublicInstance | null,
  index: number,
) => {
  progressBars.value[index] = el as HTMLDivElement | null;
};

const swiperRef = ref<any>(null);
const isPaused = ref(false);
const videoRefs = ref<(HTMLVideoElement | null)[]>([]);
const isMuted = ref(false);
const mediaLoaded = ref<boolean[]>([]);
const volume = ref(1); // Volume de 0 a 1
const showVolumeBar = ref(false);
let volumeBarTimeout: ReturnType<typeof setTimeout> | null = null;

// Controle customizado do timer (usaremos CSS keyframes para o progresso)
const autoplayDelay = ref(5000);
const defaultImageDelay = 5000;

// helper to set video refs from template
const setVideoRef = (
  el: Element | ComponentPublicInstance | null,
  index: number,
) => {
  videoRefs.value[index] = el as HTMLVideoElement | null;
};

// Pausa todos os vídeos
const pauseAllVideos = () => {
  videoRefs.value.forEach((video) => {
    if (video) {
      video.pause();
    }
  });
};

// Controla o vídeo do slide atual
const handleCurrentVideo = () => {
  const currentStoryData = stories.value[currentSlide.value];

  if (currentStoryData?.media_type === 'video') {
    const video = videoRefs.value[currentSlide.value];

    if (video) {
      // Aplica o estado de mute ao vídeo
      video.muted = isMuted.value;
      // Aplica o volume ao vídeo
      video.volume = volume.value;
      // Reinicia o vídeo do começo
      video.currentTime = 0;
      // Toca o vídeo
      video.play().catch((error) => {
        console.error('Error playing video:', error);
      });
    }
  }
};

// Atualiza o autoplayDelay baseado no tipo de mídia
const updateAutoplayDelay = () => {
  const currentStoryData = stories.value[currentSlide.value];

  if (!currentStoryData) return;

  if (currentStoryData.media_type === 'video') {
    const video = videoRefs.value[currentSlide.value];

    if (video && video.duration && !isNaN(video.duration)) {
      // Usa a duração do vídeo em milissegundos
      autoplayDelay.value = Math.floor(video.duration * 1000);
    } else {
      // Fallback para 10 segundos se não conseguir pegar a duração
      autoplayDelay.value = 10000;
    }
  } else {
    // Para imagens, usa o delay padrão
    autoplayDelay.value = defaultImageDelay;
  }
};

// Reinicia e inicia a animação do progresso usando CSS keyframes
const resetCssProgress = () => {
  // Atualiza o delay baseado no tipo de mídia atual
  updateAutoplayDelay();

  // Stop previous animations & set static state
  progressBars.value.forEach((el, idx) => {
    if (!el) return;
    el.style.animation = 'none';
    el.style.transform = idx < currentSlide.value ? 'scaleX(1)' : 'scaleX(0)';
  });

  // Só inicia a animação se a mídia já estiver carregada
  if (!mediaLoaded.value[currentSlide.value]) {
    return;
  }

  // Start animation on the current element
  const current = progressBars.value[currentSlide.value];

  if (current) {
    // force reflow so animation restarts when re-applied
    void current.offsetWidth; // eslint-disable-line no-void
    current.style.setProperty(
      '--autoplay-duration',
      `${autoplayDelay.value}ms`,
    );
    current.style.animation = `ap-progress ${autoplayDelay.value}ms linear forwards`;
    current.style.animationPlayState = isPaused.value ? 'paused' : 'running';
  }
};

const stopCustomAutoplay = () => {
  // Remove any animation from current
  progressBars.value.forEach((el) => {
    if (!el) return;
    el.style.animation = 'none';
  });
};

// NOTE: removed resetProgress compatibility wrapper — use resetCssProgress()

// Handler para quando a imagem carrega
const onImageLoad = (index: number) => {
  mediaLoaded.value[index] = true;

  if (index === currentSlide.value) {
    nextTick(() => resetCssProgress());
  }
};

// Handler para quando o vídeo carrega os metadados (duração disponível)
const onVideoLoadedMetadata = (index: number) => {
  mediaLoaded.value[index] = true;

  // Se for o vídeo atual, atualiza o delay e reinicia a progress bar
  if (index === currentSlide.value) {
    const video = videoRefs.value[index];

    if (video) {
      // Aplica o estado de mute ao vídeo
      video.muted = isMuted.value;
      // Aplica o volume ao vídeo
      video.volume = volume.value;

      if (!isPaused.value) {
        video.play().catch((error) => {
          console.error('Error playing video on metadata loaded:', error);
        });
      }
    }
    nextTick(() => resetCssProgress());
  }
};

// Handler para quando o vídeo termina
const onVideoEnded = (index: number) => {
  if (index === currentSlide.value) {
    onProgressEnd(index);
  }
};

// Handler para quando o vídeo falha ao carregar (safety net)
const onVideoError = (index: number) => {
  // Remove o story com vídeo indisponível da lista
  stories.value.splice(index, 1);

  // Se não sobrou nenhum story, fecha
  if (stories.value.length === 0) {
    closeStories();

    return;
  }

  // Ajusta o slide atual se necessário
  if (currentSlide.value >= stories.value.length) {
    currentSlide.value = stories.value.length - 1;
  }

  nextTick(() => {
    handleCurrentVideo();
    resetCssProgress();
  });
};

// Controla o volume do vídeo
const handleVolumeChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newVolume = parseFloat(target.value);

  volume.value = newVolume;

  // Se ajustar o volume acima de 0, desmuta automaticamente
  if (newVolume > 0 && isMuted.value) {
    isMuted.value = false;
  }

  // Se ajustar para 0, muta
  if (newVolume === 0 && !isMuted.value) {
    isMuted.value = true;
  }

  const currentStoryData = stories.value[currentSlide.value];

  if (currentStoryData?.media_type === 'video') {
    const video = videoRefs.value[currentSlide.value];

    if (video) {
      video.volume = newVolume;
      video.muted = isMuted.value;
    }
  }

  // Reset do timeout - se não mexer por 3000ms, fecha a barra
  if (volumeBarTimeout) clearTimeout(volumeBarTimeout);
  volumeBarTimeout = setTimeout(() => {
    showVolumeBar.value = false;
  }, 3000);
};

// Cancela o timeout quando o mouse entra na barra
const handleVolumeBarMouseEnter = () => {
  if (volumeBarTimeout) clearTimeout(volumeBarTimeout);
};

// Inicia o timeout quando o mouse sai da barra
const handleVolumeBarMouseLeave = () => {
  volumeBarTimeout = setTimeout(() => {
    showVolumeBar.value = false;
  }, 3000);
};

// Toggle volume: muta/desmuta E mostra/esconde a barra
const toggleVolumeControl = (event: MouseEvent) => {
  event.stopPropagation();

  // Cancela o timeout anterior
  if (volumeBarTimeout) clearTimeout(volumeBarTimeout);

  // Se estiver mutado, desmuta, ajusta volume e mostra a barra
  if (isMuted.value) {
    isMuted.value = false;
    // Se o volume está em 0, ajusta para um valor mínimo
    if (volume.value === 0) {
      volume.value = 0.5;
    }
    showVolumeBar.value = true;

    const currentStoryData = stories.value[currentSlide.value];

    if (currentStoryData?.media_type === 'video') {
      const video = videoRefs.value[currentSlide.value];

      if (video) {
        video.muted = false;
        video.volume = volume.value;
      }
    }

    // Inicia o timeout para fechar após 3s
    volumeBarTimeout = setTimeout(() => {
      showVolumeBar.value = false;
    }, 3000);
  } else {
    // Se não estiver mutado, apenas toggle a barra
    const willShow = !showVolumeBar.value;

    showVolumeBar.value = willShow;

    // Se abriu a barra, inicia o timeout
    if (willShow) {
      volumeBarTimeout = setTimeout(() => {
        showVolumeBar.value = false;
      }, 3000);
    }
  }
};

const togglePause = (event: MouseEvent) => {
  // Verifica se o clique foi na área direita (botão next - 20% da direita)
  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const isRightSide = clickX > rect.width * 0.8; // 20% da direita

  // Se estiver no último slide e clicar na área do botão next, marca como visto e fecha
  if (isRightSide && swiperRef.value?.isEnd) {
    markStoriesAsSeen();
    closeStories();

    return;
  }

  isPaused.value = !isPaused.value;

  // Pausa/retoma o vídeo se for um vídeo
  const currentStoryData = stories.value[currentSlide.value];

  if (currentStoryData?.media_type === 'video') {
    const video = videoRefs.value[currentSlide.value];

    if (video) {
      if (isPaused.value) {
        video.pause();
      } else {
        video.play();
      }
    }
  }
};

const { trackClick, trackView } = useStoryTracking();

const router = useRouter();
const route = useRoute();

// Remove o query param ?open=story do histórico antes de navegar
const cleanStoryQueryParam = async () => {
  if (route.query.open === 'story') {
    const query = { ...route.query };

    delete query.open;
    try {
      await router.replace({ query });
    } catch {
      // Ignora falhas no replace (navigation guard, etc.) — a navegação principal prossegue
    }
  }
};

// Função para abrir link do story (navega internamente ou abre externa)
const openStoryLink = async (linkUrl: string | null, event: MouseEvent) => {
  event.stopPropagation();
  if (!linkUrl) return;

  // Registra o clique antes de redirecionar
  if (currentStory.value?.id) {
    trackClick(currentStory.value.id);
  }

  // Externo cobre também subdomínios do app (encurtador go.apostatudo.bet.br,
  // blog etc.) — só o host do app e o domínio raiz de prod navegam por rota.
  if (isExternalAppUrl(linkUrl)) {
    window.open(linkUrl, '_blank', 'noopener,noreferrer');

    return;
  }

  // Remove ?open=story do histórico para não reabrir ao voltar
  await cleanStoryQueryParam();
  navigateTo(resolveAppLink(linkUrl));
  closeStories();
};

const onSwiperInit = (event: any) => {
  swiperRef.value = event.detail[0];

  // Vai para o último slide visto (se houver)
  if (lastSlideIndex.value > 0 && lastSlideIndex.value < stories.value.length) {
    swiperRef.value.slideTo(lastSlideIndex.value, 0);
    currentSlide.value = lastSlideIndex.value;
  }

  // Registra a visualização do slide inicial
  const initialStory = stories.value[currentSlide.value];

  if (initialStory?.id) {
    trackView(initialStory.id);
  }

  // Inicia o vídeo se for vídeo
  nextTick(() => {
    handleCurrentVideo();
    // Só inicia a progress bar se a mídia já estiver carregada
    // Se não estiver, será iniciada pelo evento onLoad/onLoadedMetadata
    if (mediaLoaded.value[currentSlide.value]) {
      resetCssProgress();
    }
  });
};

let closeSetTimeout: ReturnType<typeof setTimeout> | null = null;

const closeStories = () => {
  stopCustomAutoplay();
  pauseAllVideos();
  if (closeSetTimeout) clearTimeout(closeSetTimeout);

  closeSetTimeout = setTimeout(() => {
    emit('close');
  }, 100);
};

const swiperOptions = computed(() => {
  return {
    autoplay: false, // Desabilita o autoplay nativo do Swiper
    slidesPerView: 1,
    navigation: true,
  };
});

const onSlideChange = (swiper: any) => {
  currentSlide.value = swiper.detail[0].realIndex;

  // Pausa todos os vídeos antes de mudar de slide
  pauseAllVideos();

  // Retoma o autoplay quando muda de slide manualmente
  isPaused.value = false;

  // Fecha a barra de volume ao mudar de slide
  showVolumeBar.value = false;

  // Registra a visualização do novo slide
  const story = stories.value[currentSlide.value];

  if (story?.id) {
    trackView(story.id);
  }

  // Salva o progresso do slide atual
  saveLastSlideIndex(currentSlide.value);

  // Controla o vídeo do novo slide atual
  nextTick(() => {
    handleCurrentVideo();
    // Só inicia a progress bar se a mídia já estiver carregada
    // Se não estiver, será iniciada pelo evento onLoad/onLoadedMetadata
    if (mediaLoaded.value[currentSlide.value]) {
      resetCssProgress();
    }
  });
};

watch(isPaused, (paused) => {
  const el = progressBars.value[currentSlide.value];

  if (!el) return;
  el.style.animationPlayState = paused ? 'paused' : 'running';
});

// Atualiza a variável CSS do volume para o progresso visual
watch(volume, (newVolume) => {
  if (import.meta.client) {
    document.documentElement.style.setProperty(
      '--volume-percent',
      `${newVolume * 100}%`,
    );
  }
});

// Computed para pegar o story atual
const currentStory = computed(() => stories.value[currentSlide.value]);

// Função para adicionar parâmetros de qualidade à URL do vídeo
const getVideoUrl = (url: string) => {
  if (!url) return url;

  // Se for Cloudflare Stream, adiciona parâmetros para forçar qualidade alta
  if (
    url.includes('videodelivery.net') ||
    url.includes('cloudflarestream.com')
  ) {
    const separator = url.includes('?') ? '&' : '?';

    // startBitrate: força começar com bitrate alto (3000 kbps = ~1080p)
    // clientBandwidthHint: simula conexão rápida (10 Mbps)
    return `${url}${separator}startBitrate=3000&clientBandwidthHint=10000`;
  }

  return url;
};

// Inicializa a variável CSS do volume quando o componente é montado
onMounted(() => {
  if (import.meta.client) {
    document.documentElement.style.setProperty(
      '--volume-percent',
      `${volume.value * 100}%`,
    );
  }
});

onUnmounted(() => {
  stopCustomAutoplay();
  // Limpa o timeout ao desmontar
  if (volumeBarTimeout) clearTimeout(volumeBarTimeout);
});
</script>

<template>
  <div
    class="w-screen h-[100dvh] max-h-[100dvh] min-[450px]:w-auto min-[450px]:aspect-[9/16] min-[450px]:max-w-[1080px] min-[450px]:max-h-[100dvh] relative overflow-hidden stories-container flex items-center justify-center"
  >
    <!-- TOP SHADOW -->
    <span
      class="absolute top-0 inset-x-0 w-full h-24 z-10 bg-gradient-to-b from-bg-primary/70"
    ></span>

    <!-- Barras de progresso estilo Instagram -->
    <div class="absolute top-2 left-0 right-0 px-4 z-50 flex gap-1">
      <div
        v-for="(story, index) in stories"
        :key="story.title + index"
        class="flex-1 h-1 rounded bg-white bg-opacity-30 overflow-hidden"
      >
        <div
          :ref="(el) => setProgressRef(el, index)"
          class="h-full bg-white progress-inner"
          :class="{ 'progress-done': currentSlide > index }"
          :style="{
            '--autoplay-duration': autoplayDelay + 'ms',
            animationPlayState:
              currentSlide === index && isPaused ? 'paused' : 'running',
            transform: index < currentSlide ? 'scaleX(1)' : 'scaleX(0)',
          }"
          @animationend="() => currentSlide === index && onProgressEnd(index)"
        ></div>
      </div>
    </div>

    <swiper-container
      v-if="hasStories"
      v-bind="swiperOptions"
      class="stories-swiper w-full h-full"
      @swiperinit="onSwiperInit"
      @swiperslidechange="onSlideChange"
    >
      <swiper-slide
        v-for="(story, index) in stories"
        :key="story.title + index"
        class="!flex !items-center !justify-center !w-full !h-full min-[450px]:aspect-[9/16] min-[450px]:max-w-[1080px] min-[450px]:max-h-[1920px] relative"
        @click="togglePause($event)"
      >
        <img
          v-if="story.media_type === 'image'"
          :alt="story.title"
          class="story-image"
          draggable="false"
          :src="story.imgUrl"
          @dragstart.prevent
          @load="onImageLoad(index)"
        />
        <video
          v-else-if="story.media_type === 'video'"
          :ref="(el) => setVideoRef(el, index)"
          class="story-image"
          playsinline
          preload="auto"
          :src="getVideoUrl(story.imgUrl)"
          @ended="onVideoEnded(index)"
          @error="onVideoError(index)"
          @loadedmetadata="onVideoLoadedMetadata(index)"
        />
      </swiper-slide>
    </swiper-container>

    <!-- Botão de Redirecionamento (fora do swiper para garantir clique) -->
    <button
      v-if="currentStory?.linkUrl"
      class="story-link-button"
      :style="{
        backgroundColor: currentStory.buttonColor || undefined,
        color: currentStory.buttonTextColor || undefined,
      }"
      @click="openStoryLink(currentStory.linkUrl, $event)"
    >
      {{ currentStory.buttonText || 'Saiba mais' }}
    </button>

    <!-- Overlay de Pause -->
    <div
      v-if="isPaused"
      class="absolute inset-0 z-30 flex items-center justify-center pointer-events-none opacity-50"
    >
      <div
        class="w-20 h-20 bg-black/50 rounded-full flex items-center justify-center"
      >
        <div class="flex gap-2">
          <div class="w-2 h-10 bg-white rounded"></div>
          <div class="w-2 h-10 bg-white rounded"></div>
        </div>
      </div>
    </div>

    <!-- BOTTOM SHADOW -->
    <span
      class="absolute bottom-0 inset-x-0 w-full h-24 z-10 bg-gradient-to-t from-bg-primary/70"
    ></span>

    <div class="flex gap-2 items-center absolute top-5 left-5 z-50">
      <div class="w-[30px] h-[30px]">
        <ApLogoStories />
      </div>
    </div>

    <div class="flex gap-3 items-center absolute top-7 right-7 z-50">
      <!-- Controle de Volume (só aparece em vídeos) -->
      <div
        v-if="currentStory?.media_type === 'video'"
        class="relative flex flex-col items-center gap-2"
      >
        <!-- Botão de Volume/Mute -->
        <button
          class="w-8 h-8 flex items-center justify-center text-white"
          @click="toggleVolumeControl($event)"
        >
          <!-- Ícone de Som -->
          <svg
            v-if="!isMuted"
            class="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
            />
          </svg>

          <!-- Ícone de Mute -->
          <svg
            v-else
            class="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
            />
          </svg>
        </button>

        <!-- Barra de Volume (aparece abaixo centralizada) -->
        <div
          v-if="showVolumeBar"
          class="volume-bar-container"
          @click.stop
          @mouseenter="handleVolumeBarMouseEnter"
          @mouseleave="handleVolumeBarMouseLeave"
        >
          <input
            class="volume-slider"
            max="1"
            min="0"
            step="0.01"
            type="range"
            :value="volume"
            @input="handleVolumeChange"
          />
        </div>
      </div>

      <!-- Botão de Fechar -->
      <button @click="$emit('close')">
        <NuxtIcon
          :filled="true"
          name="apostatudo/close"
        />
      </button>
    </div>
  </div>
</template>

<style>
.stories-container {
  overflow: hidden !important;
  max-height: 100dvh !important;
  height: 100dvh !important;
}

.stories-swiper {
  overflow: hidden !important;
  max-height: 100% !important;
}

.story-image {
  width: 100%;
  height: 100%;
  max-height: 100dvh;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  user-select: none;
}

.stories-swiper::part(button-next),
.stories-swiper::part(button-prev) {
  width: 20%;
  height: 70%;
  top: 0;
  margin-top: 0;
  background: transparent;
  color: transparent;
}

.stories-swiper::part(button-next)::after,
.stories-swiper::part(button-prev)::after {
  display: none;
}

.stories-swiper::part(button-prev) {
  left: 0;
  right: auto;
}

.stories-swiper::part(button-next) {
  right: 0;
  left: auto;
}

.progress-inner {
  transform-origin: left;
  will-change: transform;
}

.progress-done {
  transform: scaleX(1);
  animation: none !important;
}

@keyframes ap-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.story-link-button {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  padding: 12px 24px;
  background-color: white;
  color: black;
  font-weight: 600;
  border-radius: 9999px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 140px;
  text-align: center;
  transition: all 0.2s;
  bottom: max(0%, env(safe-area-inset-bottom, 20px) + 20px);
}

.story-link-button:hover {
  background-color: rgba(255, 255, 255, 0.9);
}

.story-link-button:active {
  transform: translateX(-50%) scale(0.95);
}

@media (min-width: 450px) {
  .story-link-button {
    bottom: 10%;
  }
}

/* Controle de Volume */
.volume-bar-container {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  height: 100px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 16px;
  padding: 8px 0;
  backdrop-filter: blur(8px);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 80px;
  height: 4px;
  background: transparent;
  outline: none;
  border-radius: 2px;
  transform: rotate(-90deg);
  cursor: pointer;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin-top: -5px;
}

.volume-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.volume-slider::-webkit-slider-runnable-track {
  background: linear-gradient(
    to right,
    white 0%,
    white var(--volume-percent, 100%),
    rgba(255, 255, 255, 0.3) var(--volume-percent, 100%),
    rgba(255, 255, 255, 0.3) 100%
  );
  height: 4px;
  border-radius: 2px;
}

.volume-slider::-moz-range-track {
  background: rgba(255, 255, 255, 0.3);
  height: 4px;
  border-radius: 2px;
}

.volume-slider::-moz-range-progress {
  background: white;
  height: 4px;
  border-radius: 2px;
}
</style>
